import { EditPage, EditPageTextField, EditPageCheckbox, EditPageSelect, EditPagePanel } from './../../../common/EditPage';
import SelectContainer from '../../../common/SelectContainer';
import { update, getOne } from './../../../common/v4/BackendProvider';
import { ToastContainer, toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';

const MailAccountEdit = () => {
    const navigate = useNavigate();
    const { account_id } = useParams();
    const [meta, setMeta] = useState({});
    const [data, setData] = useState(null);

    useEffect(() => {
        getOne('/admin/mail-accounts/','new').then((res) => setMeta(res.data.data)).catch(()=>{});
        getOne('/admin/mail-accounts/', account_id).then((res) => setData(res.data.data)).catch((e)=>{
            toast.error('Failed to load mail account');
            navigate('/settings/mail_accounts');
        });
    }, [account_id]);

    const handleSave = () => {
        update(`/admin/mail-accounts/`, account_id, {data: data}).then(response => {
            toast.success("Mail account updated", {});
            navigate('/settings/mail_accounts');
        }).catch(error => {
            toast.error("Error updating mail account: " + (error?.response?.data?.message || error.message), {});
        });
    }

    const handleCancel = () => {
        navigate('/settings/mail_accounts');
    }

    const onChange = (id, value) => {
        setData({ ...data, [id]: value });
        
        // Auto-fill settings for common providers
        if (id === 'provider') {
            if (value === 'gmail') {
                setData(prev => ({
                    ...prev,
                    smtp_host: 'smtp.gmail.com',
                    smtp_port: '587',
                    smtp_encryption: 'tls',
                    imap_host: 'imap.gmail.com',
                    imap_port: '993',
                    imap_encryption: 'ssl'
                }));
            } else if (value === 'outlook') {
                setData(prev => ({
                    ...prev,
                    smtp_host: 'smtp-mail.outlook.com',
                    smtp_port: '587',
                    smtp_encryption: 'tls',
                    imap_host: 'outlook.office365.com',
                    imap_port: '993',
                    imap_encryption: 'ssl'
                }));
            } else if (value === 'yahoo') {
                setData(prev => ({
                    ...prev,
                    smtp_host: 'smtp.mail.yahoo.com',
                    smtp_port: '587',
                    smtp_encryption: 'tls',
                    imap_host: 'imap.mail.yahoo.com',
                    imap_port: '993',
                    imap_encryption: 'ssl'
                }));
            } else if (value === 'custom') {
                setData(prev => ({
                    ...prev,
                    smtp_host: '',
                    smtp_port: '',
                    smtp_encryption: 'tls',
                    imap_host: '',
                    imap_port: '',
                    imap_encryption: 'ssl'
                }));
            }
        }
    }

    if (!data) return null;

    return (
        <div>
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} closeOnClick pauseOnHover draggable progress="undefined" />
            <EditPage handleSave={ handleSave } handleCancel={ handleCancel } title={`Edit Mail Account: ${data.name}`} >
                <EditPagePanel className="col-lg-6">
                    <EditPageTextField label="Name" id="name" onChange={ onChange } value={ data.name } />
                    <EditPageTextField label="Email" id="email" onChange={ onChange } value={ data.email } />
                    <SelectContainer label="Provider" id="provider" onChange={ onChange } value={ data.provider } options={meta.providers} />
                    <EditPageCheckbox label="SMTP Enabled" id="smtp_enabled" onChange={ onChange } value={ data.smtp_enabled } />
                    <EditPageCheckbox label="IMAP Enabled" id="imap_enabled" onChange={ onChange } value={ data.imap_enabled } />
                </EditPagePanel>
                <EditPagePanel className="col-lg-6">
                    <EditPageCheckbox label="Active" id="is_active" onChange={ onChange } value={ data.is_active } />
                    <EditPageTextField label="Description" id="description" onChange={ onChange } value={ data.description || '' } />
                </EditPagePanel>
                
                {data.smtp_enabled && (
                    <EditPagePanel className="col-lg-12" title="SMTP Settings (Outgoing)">
                        <div className="row">
                            <div className="col-lg-6">
                                <SelectContainer label="Encryption" id="smtp_encryption" onChange={ onChange } value={ data.smtp_encryption || '' } options={meta.encryption} />
                                <EditPageTextField label="Host" id="smtp_host" onChange={ onChange } value={ data.smtp_host || '' } placeholder="Leave empty for Gmail/Outlook/Yahoo" />
                                <EditPageTextField label="Port" id="smtp_port" onChange={ onChange } value={ data.smtp_port || '' } placeholder="Leave empty for Gmail/Outlook/Yahoo" />
                            </div>
                            <div className="col-lg-6">
                                <EditPageTextField label="Username" id="smtp_username" onChange={ onChange } value={ data.smtp_username || '' } placeholder="Leave empty for Gmail/Outlook/Yahoo" />
                                <EditPageTextField label="Password" id="smtp_password" onChange={ onChange } value={ data.smtp_password || '' } type="password" placeholder="Leave empty to keep current password" />
                            </div>
                        </div>
                    </EditPagePanel>
                )}
                
                {data.imap_enabled && (
                    <EditPagePanel className="col-lg-12" title="IMAP Settings (Incoming)">
                        <div className="row">
                            <div className="col-lg-6">
                                <SelectContainer label="Encryption" id="imap_encryption" onChange={ onChange } value={ data.imap_encryption || '' } options={meta.encryption} />
                                <EditPageTextField label="Host" id="imap_host" onChange={ onChange } value={ data.imap_host || '' } placeholder="Leave empty for Gmail/Outlook/Yahoo" />
                                <EditPageTextField label="Port" id="imap_port" onChange={ onChange } value={ data.imap_port || '' } placeholder="Leave empty for Gmail/Outlook/Yahoo" />
                            </div>
                            <div className="col-lg-6">
                                <EditPageTextField label="Username" id="imap_username" onChange={ onChange } value={ data.imap_username || '' } placeholder="Leave empty for Gmail/Outlook/Yahoo" />
                                <EditPageTextField label="Password" id="imap_password" onChange={ onChange } value={ data.imap_password || '' } type="password" placeholder="Leave empty to keep current password" />
                            </div>
                        </div>
                    </EditPagePanel>
                )}
                
                {data.provider === 'gmail' && (
                    <EditPagePanel className="col-lg-12">
                        <div style={{ padding: '10px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                            <strong>Gmail Setup Instructions:</strong>
                            <ul style={{ margin: '10px 0', paddingLeft: '20px' }}>
                                <li>Enable 2-Factor Authentication on your Google account</li>
                                <li>Generate an "App Password" from Google Account settings</li>
                                <li>Use your full Gmail address as username</li>
                                <li>Use the generated app password (not your regular password)</li>
                            </ul>
                            <a href="https://support.google.com/answer/185833" target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>
                                How to generate an App Password →
                            </a>
                        </div>
                    </EditPagePanel>
                )}
            </EditPage>
        </div>
    )
}

export default MailAccountEdit;
